import React, { useCallback, useMemo, useEffect } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useDispatch } from "react-redux";

import {
	Button,
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectAsyncFormGroup,
	HookSelectFormGroup,
	CloseButton,
} from "components";
import { DebounceTimeout } from "globalConstants";
import { getAgentFullNameStatus } from "helpers";
import { isInactiveAgentStatus } from "mappers";
import { adjustmentsService, dropdownValuesService } from "services";
import { uiActions } from "store";
import { trimNormalizer } from "validators";
import { currency, required } from "validators/validation";

const AdjustmentModal = ({
	show,
	onHide,
	backdrop,
	dialogAs,
	title,
	paidYear,
	paidMonth,
	adjustment,
	isEdit = false,
	refetchAdjustmentsList,
}) => {
	const dispatch = useDispatch();
	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues: {
			adjustmentAgent: null,
			adjustmentAmountSymbol: "+",
			category: null,
		},
	});

	const { register, handleSubmit, formState, setValue, reset, errors } =
		formMethods;

	useEffect(() => {
		if (adjustment) {
			const values = {
				...adjustment,
				adjustmentAgent: {
					agent: { ...adjustment.agent },
					agent_id: adjustment.agent_id,
				},
				amount: Math.abs(adjustment?.amount),
			};
			reset(values);
		}
	}, [reset, adjustment]);

	const { mutateAsync: loadAgents } = useMutation(
		async ({ search, prevOptions, page }) => {
			const agents = await dropdownValuesService.getAdjustmentAgents({
				paidYear,
				paidMonth,
				search,
				page,
			});

			const hasMore =
				prevOptions.length + agents.items.length < agents.count;

			return {
				options: agents.items,
				hasMore,
				additional: {
					page: page + 1,
				},
			};
		},
	);

	const {
		data: adjustmentCategories,
		isFetching: isLoadingAdjustmentCategories,
	} = useQuery(
		["adjustmentCategories"],
		() => dropdownValuesService.getAdjustmentCategories(),
		{
			onSuccess(result) {
				if (adjustment) {
					const selectedCategory = result?.find(
						item => item.displayName === adjustment?.category_name,
					);
					setValue("category", selectedCategory);
				}
			},
		},
	);

	const {
		mutateAsync: submitAddAdjustment,
		isLoading: isSubmittingAddAdjustment,
	} = useMutation(adjustmentsService.addAdjustment, {
		onMutate() {
			dispatch(uiActions.showSpinnerOverlay());
		},
		onSuccess(result) {
			if (result.error) {
				dispatch(
					uiActions.showErrorNotification({
						title: result.error,
					}),
				);
				return;
			}

			dispatch(
				uiActions.showSuccessNotification({
					title: "Adjustment saved successfully",
					delay: 5000,
				}),
			);
			refetchAdjustmentsList();
		},
		onError(error) {
			dispatch(
				uiActions.showErrorNotification({
					title: error.Message,
				}),
			);
		},
		onSettled() {
			dispatch(uiActions.hideSpinnerOverlay());
		},
	});

	const {
		mutateAsync: submitEditAdjustment,
		isLoading: isSubmittingEditAdjustment,
	} = useMutation(adjustmentsService.editAdjustment, {
		onMutate() {
			dispatch(uiActions.showSpinnerOverlay());
		},
		onSuccess(result) {
			if (result.error) {
				dispatch(
					uiActions.showErrorNotification({
						title: result.error,
					}),
				);
				return;
			}

			dispatch(
				uiActions.showSuccessNotification({
					title: "Adjustment has been successfully updated",
					delay: 3000,
				}),
			);
			refetchAdjustmentsList();
		},
		onError(error) {
			dispatch(
				uiActions.showErrorNotification({
					title: error.detail,
				}),
			);
		},
		onSettled() {
			dispatch(uiActions.hideSpinnerOverlay());
		},
	});

	const onSubmit = useCallback(
		formValues => {
			try {
				const model = {
					paidYear,
					paidMonth,
					agentId: formValues?.adjustmentAgent?.agent_id,
					amount:
						formValues?.adjustmentAmountSymbol + formValues?.amount,
					category: formValues?.category?.value,
					comment: formValues?.comment,
				};
				if (isEdit) {
					submitEditAdjustment({ model, id: adjustment?.id });
				} else {
					submitAddAdjustment(model);
				}
			} finally {
				onHide();
			}
		},
		[
			paidYear,
			paidMonth,
			isEdit,
			submitEditAdjustment,
			adjustment?.id,
			submitAddAdjustment,
			onHide,
		],
	);

	const isSaveDisabled = useMemo(
		() =>
			isSubmittingAddAdjustment ||
			isSubmittingEditAdjustment ||
			!formState.isDirty,
		[
			formState.isDirty,
			isSubmittingAddAdjustment,
			isSubmittingEditAdjustment,
		],
	);

	const isDebitChecked =
		adjustment && Math.sign(adjustment?.amount) === -1 ? "-" : "";
	const isCreditChecked =
		adjustment && Math.sign(adjustment?.amount) === 1 ? "+" : "";

	return (
		<Modal
			show={show}
			onHide={onHide}
			animation={false}
			centered
			dialogAs={dialogAs}
			backdrop={backdrop}
			size="lg"
		>
			<Modal.Header>
				<Modal.Title>{title}</Modal.Title>
				<CloseButton onClick={onHide} />
			</Modal.Header>
			<Modal.Body>
				<FormProvider {...formMethods}>
					<form id="adjustmentForm" onSubmit={handleSubmit(onSubmit)}>
						<Row>
							<Col md={{ span: 6, offset: 3 }}>
								<HookSelectAsyncFormGroup
									rules={{
										validate: {
											required,
										},
									}}
									label={"Apply To"}
									loadOptions={(
										search,
										prevOptions,
										{ page },
									) =>
										loadAgents({
											search,
											prevOptions,
											page,
										})
									}
									defaultValue=""
									isSearchable
									getOptionLabel={option =>
										getAgentFullNameStatus(option.agent)
									}
									getOptionValue={option => option.agent_id}
									labelSize={{ xs: 12 }}
									controlSize={{ xs: 12 }}
									placeholder="Please Select"
									name={"adjustmentAgent"}
									showRequiredMark
									cacheOptions
									defaultOptions
									debounceTimeout={DebounceTimeout}
									additional={{ page: 0 }}
									styles={{
										option: (provided, { data }) => {
											const inactive =
												isInactiveAgentStatus(
													data?.agent?.status?.value,
												);
											return {
												...provided,
												fontStyle: inactive
													? "italic"
													: "normal",
												opacity: inactive ? "0.8" : "1",
											};
										},
									}}
								/>
								<HookFormInputGroup
									register={register}
									name="amount"
									label="Adjustment Amount, $"
									colLabel={12}
									colInput={12}
									errors={errors}
									validator={{
										required,
									}}
									normalize={currency}
									showRequiredMark
								/>
								<Row className="pb-2">
									<Col xs={4}>
										<HookFormRadioButton
											register={register}
											value="+"
											name="adjustmentAmountSymbol"
											label="Credit (+)"
											id="creditRadioButton"
											defaultChecked={isCreditChecked}
										/>
									</Col>
									<Col xs={4}>
										<HookFormRadioButton
											register={register}
											value="-"
											name="adjustmentAmountSymbol"
											label="Debit (-)"
											id="debitRadioButton"
											defaultChecked={isDebitChecked}
										/>
									</Col>
								</Row>
								<HookSelectFormGroup
									register={register}
									rules={{
										validate: {
											required,
										},
									}}
									label="Adjustment Reason"
									options={adjustmentCategories}
									getOptionLabel={option =>
										option.displayName
									}
									getOptionValue={option => option.value}
									placeholder="Please Select"
									name="category"
									cacheOptions
									defaultOptions
									debounceTimeout={DebounceTimeout}
									colLabel={{ sm: 12 }}
									colSelect={{ sm: 12 }}
									loading={isLoadingAdjustmentCategories}
									showRequiredMark
								/>
								<HookFormInputGroup
									register={register}
									defaultValue=""
									name="comment"
									label="Comment"
									placeholder="Give a brief description of adjustment"
									colInput={12}
									colLabel={12}
									rows={6}
									as="textarea"
									normalizeOnBlur={trimNormalizer}
								/>
							</Col>
						</Row>
					</form>
				</FormProvider>
			</Modal.Body>
			<Modal.Footer style={{ justifyContent: "center" }}>
				<Button
					type="submit"
					form="adjustmentForm"
					value="Save"
					disabled={isSaveDisabled}
				/>
			</Modal.Footer>
		</Modal>
	);
};

export default AdjustmentModal;
